import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Gravity from '../../constants/Gravity';
import Icon from '../Icon/Icon';
import Menu from '../Menu/Menu';
import SelectList from '../SelectList/SelectList';

/**
 * `Menubar` is a container that displays a strip of components and highlights the selected one.
 *
 * It is typically used in {@link Tabs} and similar components where you only display content related to the context you
 * selected.
 *
 * The children can be editable, allowing the user to remove children from the menubar.
 *
 * If there is not enough room to fit all children and *collapsable* is set to `true`, the children will collapse into a
 * dropdown list.
 *
 * @property {boolean} allowRemoveLast If set to `true`, the user will be able to remove the last item in the menubar. If set
 * to `false`, the **Remove** button will be removed from the last item in the menubar and the user will not be able to
 * remove the last item. Defaults to `true`.
 * @property {node} children The child components you want to add to the `Menubar`.
 * @property {string} className The class name of the component. Will be appended to "core-menubar".
 * @property {boolean} collapsable If set to `true`, the menubar items will be collapsed to a dropdown when there is not
 * enough room to display all items. This requires that the CSS styles the menubar in such a way that the items will not
 * overflow. Defaults to `true`.
 * @property {boolean} disabled If set to `true`, the menubar will be disabled and you will not be able to select an items
 * in it. Defaults to `false`.
 * @property {boolean} editable If set to `true`, the menubar will become editable meaning that the user can remove menubar
 * items. Each item will have a **Remove** button that can be used to remove items from the menubar. Defaults to `false`.
 * @property {function} onClick A callback listener when the user clicks on a component in the menubar. The selected
 * index will be sent as second parameter to *onClick*.
 * @property {function} onRemove A callback that is called when the user has clicked on the **Remove** button for a given
 * menubar item.
 * @property {number} selected The index of the selected menubar item, 0 being the index of the first item. Only makes
 * sense when showUnderline is set to `true`.
 * @property {boolean} showUnderline If `true`, the selected menubar item will be underlined with a `hr` element. Defaults
 * to `false` (no underline).
 *
 * @see Menu, PopupMenu
 */

export default function Menubar(props) {
  const {
    allowRemoveLast,
    children = [],
    className,
    collapsable,
    disabled,
    editable,
    onDoubleClick,
    selected,
    showUnderline,
    onClick,
    onRemove,
  } = props;
  const calculatedEditable = editable && (children.length === 1 ? allowRemoveLast : true);

  const [isCollapsed, setCollapsed] = useState(false);
  const [offset, setOffset] = useState(0);
  const [width, setWidth] = useState(0);

  const refList = useRef();
  const referenceTimeout = useRef();

  const onClickHandler = (e) => {
    if (onClick) {
      const index = parseInt(e.currentTarget.getAttribute('data-index'), 10);
      onClick(e, index);
    }
  };

  const onRemoveHandler = (e) => {
    e.stopPropagation();
    if (onRemove) {
      onRemove(e, parseInt(e.currentTarget.getAttribute('data-index'), 10));
    }
  };

  const onResize = () => {
    const list = refList.current;

    if (list && list.childNodes?.length) {
      const lastChild = list.childNodes[list.childNodes.length - 1];
      const collapsed = lastChild.offsetLeft - list.offsetLeft + lastChild.clientWidth > list.clientWidth;

      setCollapsed(collapsed);
    }
  };

  const updateUnderline = () => {
    setTimeout(() => {
      const { selected } = props;
      const el = refList.current?.children?.[selected];

      if (el) {
        setOffset(el.offsetLeft - el.parentElement.offsetLeft);
        setWidth(el.offsetWidth);
      }
    }, 1);
  };

  useEffect(() => {
    if (collapsable) {
      window.addEventListener('resize', onResize);
    }
    referenceTimeout.current = setTimeout(() => {
      if (showUnderline) {
        updateUnderline();
      }
      if (collapsable) {
        onResize();
      }
    }, 250);

    return () => {
      window.removeEventListener('resize', onResize);
      if (referenceTimeout.current) {
        clearTimeout(referenceTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    if (showUnderline) {
      updateUnderline();
    }
    setTimeout(() => onResize(), 1);
  }, [children, editable, collapsable]);

  const items = React.Children.map(children, (child, index) => (
    <li
      key={index}
      className={cx('core-menubar-item', { selected: index === selected })}
      data-testid={index === selected ? `selected-${index}` : undefined}
      onClick={onClickHandler}
      data-index={index}
    >
      {child}
      {calculatedEditable && <Icon name='close' className='remove' onClick={onRemoveHandler} data-index={index} />}
    </li>
  ));

  const options =
    collapsable && isCollapsed
      ? []
          .concat(children)
          .filter((child, index) => index !== selected)
          .map((child, index) => {
            index = index >= selected ? index + 1 : index; // eslint-disable-line no-param-reassign
            return (
              <div key={index} className='collapsed-item' onClick={onClickHandler} data-index={index}>
                {child}
                {calculatedEditable && <Icon name='close' className='remove' onClick={onRemoveHandler} data-index={index} />}
              </div>
            );
          })
      : [];

  const collapsedSelector =
    options.length > 0 ? (
      <Menu
        arrowSize={0}
        className={cx('collapsed-menu', { border: showUnderline })}
        customContent
        gravity={Gravity.SOUTH}
        showOpenCloseIcon
        title={children?.[selected]?.props?.children}
        useAnchorWidth
      >
        <SelectList>{options}</SelectList>
      </Menu>
    ) : null;

  const hr =
    showUnderline && !collapsedSelector ? (
      <hr
        style={{
          transform: `translate3d(${offset}px,0,0)`,
          width: `${width}px`,
          visibility: items.length > 0 ? 'visible' : 'hidden',
        }}
      />
    ) : null;

  return (
    <div
      className={cx('core-menubar', className, { editable: calculatedEditable, collapsed: collapsedSelector, disabled })}
      onDoubleClick={onDoubleClick}
    >
      <div className='core-menubar-wrapper'>
        <ul
          ref={(c) => {
            refList.current = c;
          }}
          className='core-menubar-items'
        >
          {items}
        </ul>
      </div>
      {hr}
      {collapsedSelector}
    </div>
  );
}

Menubar.displayName = 'Menubar';

Menubar.propTypes = {
  allowRemoveLast: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  collapsable: PropTypes.bool,
  disabled: PropTypes.bool,
  editable: PropTypes.bool,
  onClick: PropTypes.func,
  onRemove: PropTypes.func,
  selected: PropTypes.number,
  showUnderline: PropTypes.bool,
};

Menubar.defaultProps = {
  allowRemoveLast: true,
  collapsable: true,
  selected: 0,
};
